import { Box, Show, useMediaQuery } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon, StarFilledIcon, StarIcon } from '@/assets/icons';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import usePermission from '@/hooks/usePermission';
import { useTourContext } from '@/store/tourContext';

export interface MenuItem {
	title: string;
	href?: string;
	submenu?: MenuItem[];
	permissions?: string[];
	id?: string; 
}

export interface FaceliftMenuProps {
	trigger: React.ReactNode;
	items: MenuItem[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onNavigate: (href?: string) => void;
	onSubmenuItemRef?: (itemTitle: string, element: HTMLElement | null) => void;
	onStarClick?: (item: MenuItem, e: React.MouseEvent) => void; 
	isStarred?: (id: string) => boolean; 
	showStars?: boolean; 
	expandSubmenuForTour?: string | null;
}

import { MenuPrimitives as CustomMenu } from '@/types/MenuPrimitives.types';

const FaceliftMenu: React.FC<FaceliftMenuProps> = ({
	trigger,
	items,
	open,
	onOpenChange,
	onMouseEnter,
	onMouseLeave,
	onNavigate,
	onSubmenuItemRef,
	onStarClick,
	isStarred,
	showStars = false,
	expandSubmenuForTour = null,
}) => {
	const { hasPermission } = usePermission();
	const { isTourActive } = useTourContext();
	const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
	const [submenuPosition, setSubmenuPosition] = useState({ x: 0, y: 0 });
	const submenuTimerRef = useRef<number | null>(null);
	const menuItemRefs = useRef<{ [key: string]: HTMLElement | null }>({});


	// During tour: open nested submenu (e.g. "Invoices" inside Accounting & Finance)
	useEffect(() => {
		if (!isTourActive) return;
		if (open && expandSubmenuForTour) {
			const hasItem = items.some(
				(i) => i.title === expandSubmenuForTour && (i.submenu?.length ?? 0) > 0,
			);
			if (hasItem) {
				// Clear any pending close timer from previous hover - prevents submenu from closing immediately
				if (submenuTimerRef.current) {
					window.clearTimeout(submenuTimerRef.current);
					submenuTimerRef.current = null;
				}
				const el = menuItemRefs.current[expandSubmenuForTour];
				if (el) {
					const rect = el.getBoundingClientRect();
					setSubmenuPosition({ x: rect.right, y: rect.top });
				}
				setOpenSubmenu(expandSubmenuForTour);
			}
		} else {
			setOpenSubmenu(null);
		}
	}, [isTourActive, open, expandSubmenuForTour, items]);

	// True between 1000px and 1200px
	const [isMidViewport] = useMediaQuery([
		'(min-width: 1000px) and (max-width: 1200px)',
	]);

	// Theme-aware colors
	const menuBg = useColorModeValue('white', 'gray.800');
	const menuBorder = useColorModeValue('gray.200', 'gray.600');
	const menuText = useColorModeValue('gray.800', 'gray.100');
	const menuHoverBg = useColorModeValue(
		'brand.solidGradient',
		'brand.solidGradient',
	);
	const menuHoverText = useColorModeValue('white', 'white');

	const handleSubmenuEnter = (itemTitle: string, targetEl?: HTMLElement) => {
		// Inform parent that we're still hovering within the composite menu
		onMouseEnter();
		if (submenuTimerRef.current) {
			window.clearTimeout(submenuTimerRef.current);
			submenuTimerRef.current = null;
		}

		// Calculate position of submenu
		const menuItemElement = targetEl || menuItemRefs.current[itemTitle] || null;
		if (menuItemElement) {
			const rect = menuItemElement.getBoundingClientRect();
			setSubmenuPosition({
				x: rect.right, // align flush to the parent menu item
				y: rect.top,
			});
		}

		setOpenSubmenu(itemTitle);
	};

	const handleSubmenuLeave = () => {
		if (submenuTimerRef.current) {
			window.clearTimeout(submenuTimerRef.current);
		}
		// Don't close submenu during tutorials
		if (!isTourActive) {
			submenuTimerRef.current = window.setTimeout(() => {
				setOpenSubmenu(null);
			}, 200);
		}
		// Allow parent to schedule close as well, but only if not in tutorial
		if (!isTourActive) {
			// onMouseLeave(); // removed this its causing the menu to close on hover
		}
	};

	return (
		<>
			<CustomMenu.Root
				open={open}
				onOpenChange={(details: { open: boolean }) => {
					// Don't close menu if submenu is open
					if (openSubmenu && !details.open) {
						return;
					}
					if (isTourActive) {
						return;
					} else {
						// Normal behavior when not in tutorial
						onOpenChange(details.open);
					}
				}}
			>
				<CustomMenu.Trigger
					_focus={{ boxShadow: 'none', outline: 'none' }}
					_focusVisible={{ boxShadow: 'none', outline: 'none' }}
					border="none"
					pointerEvents={isTourActive ? 'none' : 'auto'}
					onClick={(e: React.MouseEvent) => {
						// Prevent trigger from opening menu during tutorials
						if (isTourActive) {
							e.preventDefault();
							e.stopPropagation();
							e.nativeEvent.stopImmediatePropagation();
						}
					}}
					onPointerDown={(e: React.PointerEvent) => {
						// Prevent trigger from opening menu during tutorials
						if (isTourActive) {
							e.preventDefault();
							e.stopPropagation();
							e.nativeEvent.stopImmediatePropagation();
						}
					}}
				>
					{trigger}
				</CustomMenu.Trigger>
				<CustomMenu.Positioner>
					<CustomMenu.Content
						bg={menuBg}
						border="1px solid"
						borderColor={menuBorder}
						boxShadow="lg"
						borderRadius="md"
						p={0}
						minW={isMidViewport ? '52' : '60'}
						w="auto"
						color={menuText}
						position="relative"
						overflow="visible"
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
					>
						{/* Arrow pointing to trigger (top center) */}
						<Box
							position="absolute"
							top="-1.5"
							left="10"
							transform="rotate(45deg)"
							w="2"
							h="5"
							bg={menuBg}
							boxShadow="none"
							pointerEvents="none"
						/>
						{items.map((item) => {
							const hasNestedSubmenu = !!item.submenu?.length;

							if (!hasNestedSubmenu) {
								// Simple menu item
								const itemIsStarred =
									showStars && item.id && isStarred
										? isStarred(item.id)
										: false;
								return (
									<CustomMenu.Item
										key={item.title}
										onClick={() => onNavigate(item.href)}
										px={0}
										py={0}
										h="9"
										display="flex"
										alignItems="center"
										justifyContent="space-between"
										_hover={{
											background: menuHoverBg,
											color: menuHoverText,
										}}
										cursor="pointer"
										borderRadius={0}
									>
										<Box
											display="flex"
											alignItems="center"
											justifyContent="space-between"
											w="full"
											px="4"
										>
											<Box>{item.title}</Box>
											<Show when={showStars}>
												<Box
													ml="2"
													display="flex"
													alignItems="center"
													justifyContent="center"
													w="4"
													flexShrink={0}
													onClick={(e: React.MouseEvent) => {
														e.preventDefault();
														e.stopPropagation();
														if (onStarClick) {
															onStarClick(item, e);
														}
													}}
													cursor="pointer"
													_hover={{ opacity: 0.8 }}
												>
													<Show
														when={itemIsStarred}
														fallback={<StarIcon width={16} height={16} />}
													>
														<StarFilledIcon
															width={16}
															height={16}
															color="#FFD700"
														/>
													</Show>
												</Box>
											</Show>
										</Box>
									</CustomMenu.Item>
								);
							}

							// Menu item with nested submenu
							return (
								<CustomMenu.Item
									key={item.title}
									ref={(el: HTMLElement | null) => {
										menuItemRefs.current[item.title] = el;
									}}
									px={0}
									py={0}
									h="9"
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									_hover={{
										background: menuHoverBg,
										color: menuHoverText,
									}}
									cursor="pointer"
									borderRadius={0}
									onMouseEnter={(e) =>
										handleSubmenuEnter(
											item.title,
											e.currentTarget as HTMLElement,
										)
									}
									onMouseLeave={handleSubmenuLeave}
									onClick={item.href ? () => onNavigate(item.href) : undefined}
								>
									<Box
										display="flex"
										alignItems="center"
										justifyContent="space-between"
										w="full"
										px="4"
									>
										<Box>{item.title}</Box>
										<Box
											ml="2"
											display="flex"
											alignItems="center"
											justifyContent="center"
											w="4"
											flexShrink={0}
										>
											<ChevronRightIcon width={6} height={10} />
										</Box>
									</Box>
								</CustomMenu.Item>
							);
						})}
					</CustomMenu.Content>
				</CustomMenu.Positioner>
			</CustomMenu.Root>

			{/* Render submenu outside of Menu container */}
			<Show when={!!openSubmenu}>
				<Box
					position="fixed"
					left={`${submenuPosition.x}px`}
					top={`${submenuPosition.y}px`}
					bg={menuBg}
					border="1px solid"
					borderColor={menuBorder}
					boxShadow="lg"
					borderRadius="md"
					p={0}
					minW={isMidViewport ? '180px' : '200px'}
					w="auto"
					zIndex={9999}
					color={menuText}
					onMouseEnter={() => {
						if (openSubmenu) {
							handleSubmenuEnter(openSubmenu);
						}
						// keep parent menu open while hovering the submenu
						onMouseEnter();
					}}
					onMouseLeave={() => {
						handleSubmenuLeave();
						// Don't close menu during tutorials
						if (!isTourActive) {
							onMouseLeave();
						}
					}}
				>
					{items
						.find((item) => item.title === openSubmenu)
						?.submenu?.filter(
							(el) =>
								!el?.permissions ||
								!el?.permissions?.length ||
								hasPermission(el?.permissions || []),
						)
						.map((nestedItem) => {
							const nestedItemIsStarred =
								showStars && nestedItem.id && isStarred
									? isStarred(nestedItem.id)
									: false;
							return (
								<Box
									key={nestedItem.title}
									ref={(el: HTMLElement | null) => {
										if (onSubmenuItemRef) {
											onSubmenuItemRef(nestedItem.title, el);
										}
									}}
									px="4"
									py={0}
									h="9"
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									_hover={{
										background: menuHoverBg,
										color: menuHoverText,
									}}
									cursor="pointer"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										setTimeout(() => {
											setOpenSubmenu(null);
										}, 100);
										onNavigate(nestedItem.href);
									}}
									fontSize="sm"
									fontWeight={500}
								>
									<Box>{nestedItem.title}</Box>
									<Show when={showStars}>
										<Box
											ml="2"
											display="flex"
											alignItems="center"
											justifyContent="center"
											w="4"
											flexShrink={0}
											onClick={(e: React.MouseEvent) => {
												e.preventDefault();
												e.stopPropagation();
												if (onStarClick) {
													onStarClick(nestedItem, e);
												}
											}}
											cursor="pointer"
											_hover={{ opacity: 0.8 }}
										>
											<Show
												when={nestedItemIsStarred}
												fallback={<StarIcon width={16} height={16} />}
											>
												<StarFilledIcon
													width={16}
													height={16}
													color="#FFD700"
												/>
											</Show>
										</Box>
									</Show>
								</Box>
							);
						})}
				</Box>
			</Show>
		</>
	);
};

export default FaceliftMenu;
